import { Injectable, computed, signal } from '@angular/core'

/** Mantenemos en memoria la captura activa de proveedor, producto, fotos y telas. */

export interface TelaEnCurso {
  id: string
  muestrario: string
  colores: string[]
  tipo: string | null
  fotoFrenteBlob?: Blob
  fotoReversoBlob?: Blob
}

export interface TelaGuardadaLocal extends TelaEnCurso {
  colores: string[]
}

/** Cada foto del producto conserva su etiqueta de captura. */
export interface FotoProducto {
  id: string
  blob: Blob
  etiqueta: string
}

interface EstadoSesion {
  proveedorCodigo: string | null
  /** Opcional — puede no existir todavía si el proveedor se está creando recién. */
  proveedorNombre: string | null
  productoNum: number
  codigoProductoActual: string | null
  telaNum: number
  fotosProducto: FotoProducto[]
  telasDelProducto: TelaGuardadaLocal[]
}

const ESTADO_INICIAL: EstadoSesion = {
  proveedorCodigo: null,
  proveedorNombre: null,
  productoNum: 1,
  codigoProductoActual: null,
  telaNum: 1,
  fotosProducto: [],
  telasDelProducto: [],
}

@Injectable({ providedIn: 'root' })
export class SesionCapturaService {
  private readonly estado = signal<EstadoSesion>({ ...ESTADO_INICIAL })

  readonly proveedorCodigo = computed(() => this.estado().proveedorCodigo)
  readonly proveedorNombre = computed(() => this.estado().proveedorNombre)
  readonly productoNum = computed(() => this.estado().productoNum)
  readonly codigoProductoActual = computed(() => this.estado().codigoProductoActual)
  readonly telaNum = computed(() => this.estado().telaNum)
  readonly fotosProducto = computed(() => this.estado().fotosProducto)
  readonly telasDelProducto = computed(() => this.estado().telasDelProducto)

  /**
   * @param nombre Puede venir null cuando se está creando el proveedor recién
   *   (nuevo-proveedor.ts todavía no lo guardó) — ver actualizarNombreProveedor.
   */
  iniciarProveedor(codigo: string, nombre: string | null, numeroInicial = 1): void {
    this.estado.set({
      proveedorCodigo: codigo,
      proveedorNombre: nombre,
      productoNum: numeroInicial,
      codigoProductoActual: `${codigo}-P${String(numeroInicial).padStart(2, '0')}`,
      telaNum: 1,
      fotosProducto: [],
      telasDelProducto: [],
    })
  }

  /** Se llama tras guardar un proveedor nuevo — recién ahí se conoce su nombre. */
  actualizarProveedorNombre(nombre: string): void {
    this.estado.update((s) => ({ ...s, proveedorNombre: nombre }))
  }

  iniciarProducto(numeroInicial: number): void {
    this.estado.update((s) => ({
      ...s,
      productoNum: numeroInicial,
      codigoProductoActual: s.proveedorCodigo ? `${s.proveedorCodigo}-P${String(numeroInicial).padStart(2, '0')}` : null,
      telaNum: 1,
      fotosProducto: [],
      telasDelProducto: [],
    }))
  }

  agregarFotoProducto(blob: Blob, etiqueta = ''): void {
    this.estado.update((s) => ({
      ...s,
      fotosProducto: [...s.fotosProducto, { id: `f${Date.now()}-${s.fotosProducto.length}`, blob, etiqueta }],
    }))
  }

  renombrarFotoProducto(id: string, etiqueta: string): void {
    this.estado.update((s) => ({
      ...s,
      fotosProducto: s.fotosProducto.map((f) => (f.id === id ? { ...f, etiqueta } : f)),
    }))
  }

  eliminarFotoProducto(id: string): void {
    this.estado.update((s) => ({ ...s, fotosProducto: s.fotosProducto.filter((f) => f.id !== id) }))
  }

  agregarTela(tela: TelaGuardadaLocal): void {
    this.estado.update((s) => ({
      ...s,
      telasDelProducto: [...s.telasDelProducto, tela],
      telaNum: s.telaNum + 1,
    }))
  }

  editarTela(id: string, cambios: Partial<TelaGuardadaLocal>): void {
    this.estado.update((s) => ({
      ...s,
      telasDelProducto: s.telasDelProducto.map((t) => (t.id === id ? { ...t, ...cambios } : t)),
    }))
  }

  eliminarTela(id: string): void {
    this.estado.update((s) => ({ ...s, telasDelProducto: s.telasDelProducto.filter((t) => t.id !== id) }))
  }

  siguienteProducto(): void {
    this.estado.update((s) => {
      const num = s.productoNum + 1
      return {
        ...s,
        productoNum: num,
        codigoProductoActual: s.proveedorCodigo ? `${s.proveedorCodigo}-P${String(num).padStart(2, '0')}` : null,
        telaNum: 1,
        fotosProducto: [],
        telasDelProducto: [],
      }
    })
  }

  limpiarProveedor(): void {
    this.estado.set({ ...ESTADO_INICIAL })
  }

  /** Cargamos un producto local para corregirlo en la sesión activa. */
  entrarModoEdicion(productoId: string, numero: number, fotos: FotoProducto[], telas: TelaGuardadaLocal[]): void {
    this.estado.update((s) => ({
      ...s,
      codigoProductoActual: productoId,
      productoNum: numero,
      telaNum: telas.length + 1,
      fotosProducto: fotos,
      telasDelProducto: telas,
    }))
  }
}
